import React, {useEffect, useState} from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import {Box} from "@mui/material"
import {DATE_FORMAT} from "src/utils/constants"
import {dispatchSnackbar} from "src/v2/components/SnackBar/snackbar-bus"
import FormActions from "../../components/FormActions"
import ValidationParametersSection from "./validation-parameters-section"
import ValidationResultsSection from "./validation-results-section"
import ValidationMemoriesSection from "./validation-memories-section"
import {
    CALCULATION_STATUS,
    createInitialValidationParameters,
    getValidationViewModel,
    validationContextPropType,
    validationViewModelPropType,
} from "./validation-contract"
import {
    getDecimalValidationMessage,
    getValidationErrorMessage,
    isValidationParametersReady,
} from "./validation-utils"

const ValidationPage = ({
    onBack,
    onCalculate,
    onExport,
    onSaveEmission,
    onSaveValidation,
    viewModel = {},
    validationContext = {},
    isAdminUser = false,
    calculationErrorCode,
    authorizedCalculationErrorMessages,
    calculationStatus = CALCULATION_STATUS.IDLE,
}) => {
    const [parameters, setParameters] = useState(createInitialValidationParameters)
    const isCalculationComplete = calculationStatus === CALCULATION_STATUS.SUCCESS
    const {results, premios, memories} = getValidationViewModel(
        isCalculationComplete ? viewModel : undefined
    )
    const decimalErrors = {
        puparReferencia: getDecimalValidationMessage(parameters.puparReferencia),
        pumtmReferencia: getDecimalValidationMessage(parameters.pumtmReferencia),
        spreadCredito: getDecimalValidationMessage(parameters.spreadCredito),
    }
    const canCalculate = Boolean(onCalculate)
        && calculationStatus !== CALCULATION_STATUS.LOADING
        && isValidationParametersReady(parameters)
    const calculationErrorMessage = calculationErrorCode
        ? getValidationErrorMessage(
            isAdminUser,
            calculationErrorCode,
            authorizedCalculationErrorMessages
        )
        : ""

    const changeParameter = (field) => (event) => {
        setParameters((current) => ({
            ...current,
            [field]: event.target.value,
        }))
    }

    const changeReferenceDate = (value) => {
        const selectedDate = value ? dayjs(value) : null

        setParameters((current) => ({
            ...current,
            dataReferencia: selectedDate?.isValid()
                ? selectedDate.format(DATE_FORMAT)
                : "",
        }))
    }

    const calculationPayload = {parameters, validationContext}
    const validationResultPayload = {
        ...calculationPayload,
        results,
        premios,
        memories,
    }
    const emissionCharacteristicsPayload = {
        validationContext,
        spreadEmissao: results.spreadEmissao,
        prazoMedioEmissao: results.prazoMedioEmissao,
    }
    const handleExport = isCalculationComplete && onExport
        ? () => onExport(validationResultPayload)
        : undefined
    const handleSaveEmission = isCalculationComplete && onSaveEmission
        ? () => onSaveEmission(emissionCharacteristicsPayload)
        : undefined
    const handleSaveValidation = isCalculationComplete && onSaveValidation
        ? () => onSaveValidation(validationResultPayload)
        : undefined

    useEffect(() => {
        if (!calculationErrorMessage) return

        dispatchSnackbar(calculationErrorMessage, "error")
    }, [calculationErrorMessage])

    return (
        <Box sx={{pb: 16, px: {xxxs: 2, md: 3, lg: 0}}}>
            <ValidationParametersSection
                parameters={parameters}
                decimalErrors={decimalErrors}
                onParameterChange={changeParameter}
                onReferenceDateChange={changeReferenceDate}
                onCalculate={canCalculate ? () => onCalculate(calculationPayload) : undefined}
                canCalculate={canCalculate}
            />
            <ValidationResultsSection
                calculationStatus={calculationStatus}
                results={results}
                premios={premios}
                onExport={handleExport}
                onSaveEmission={handleSaveEmission}
                onSaveValidation={handleSaveValidation}
            />
            <ValidationMemoriesSection memories={memories} premios={premios}/>

            <FormActions
                showBack
                onBack={onBack}
                showDelete={false}
                showSave={false}
                showPublish={false}
            />
        </Box>
    )
}

ValidationPage.propTypes = {
    onBack: PropTypes.func.isRequired,
    onCalculate: PropTypes.func,
    onExport: PropTypes.func,
    onSaveEmission: PropTypes.func,
    onSaveValidation: PropTypes.func,
    isAdminUser: PropTypes.bool,
    calculationErrorCode: PropTypes.string,
    authorizedCalculationErrorMessages: PropTypes.objectOf(PropTypes.string),
    calculationStatus: PropTypes.oneOf(Object.values(CALCULATION_STATUS)),
    viewModel: validationViewModelPropType,
    validationContext: validationContextPropType,
}

export default ValidationPage
